/**
 * Generic helpers to reshape Elasticsearch aggregation buckets into Chart.js
 * `{ labels, datasets }` — the piece a reactive-json YAML store can't express
 * (algorithmic array assembly across nested buckets). Graine du futur E3 :
 * réutilisable pour tout widget chart alimenté par un agrégat ES. Précédent du
 * pattern : `wsolutionDashboardProcessor.js` (buildCallsData).
 *
 * Couleurs = tokens du design system « Cosmos » (`--chart-1..8` de index.css),
 * résolus au runtime — aucune couleur codée en dur.
 */

/** Résout les 8 couleurs de séries du DS depuis les variables CSS `--chart-N`. */
function dsSeriesColors() {
  // Miroir des tokens `--chart-1..8` de front_app/src/index.css — utilisé UNIQUEMENT
  // si getComputedStyle est indisponible (SSR/tests). GARDER SYNCHRONISÉ avec le DS ;
  // en runtime navigateur, ce sont toujours les variables CSS live qui priment.
  const fallback = [
    '#5a4fe0', '#22d3ee', '#10b981', '#f59e0b',
    '#f43f5e', '#8b5cf6', '#0ea5e9', '#f97316',
  ];
  if (typeof window === 'undefined' || !window.getComputedStyle) {
    return fallback;
  }
  const style = window.getComputedStyle(document.documentElement);
  return fallback.map((hex, i) => {
    const v = style.getPropertyValue(`--chart-${i + 1}`).trim();
    return v || hex;
  });
}

/** Palette cyclique basée sur les tokens DS (index → couleur). */
export function dsPalette() {
  const colors = dsSeriesColors();
  return (i) => colors[i % colors.length];
}

/**
 * Série unique depuis une liste de buckets (ex. date_histogram).
 * @param {Array} buckets  buckets ES.
 * @param {object} opts    { labelFor(b), valueFor(b), label, color }.
 */
export function singleSeries(buckets, { labelFor, valueFor, label, color }) {
  return {
    labels: (buckets || []).map(labelFor),
    datasets: [{
      label,
      data: (buckets || []).map(valueFor),
      backgroundColor: color,
      borderColor: color,
      borderWidth: 0,
    }],
  };
}

/**
 * Histogramme empilé : buckets externes (ex. date_histogram par mois), chacun
 * portant un sous-agg `terms` (ex. by_stage). Une série par clé de terme.
 * @param {Array} buckets    buckets externes.
 * @param {object} opts      { innerKey, labelFor(b), seriesLabels?, palette?, valueOf? }.
 *                           getValue(subBucket) → valeur de série (défaut : doc_count).
 *                           NB : ne PAS nommer cette option `valueOf` (méthode
 *                           héritée d'Object.prototype → capturée à tort en déstructuration).
 */
export function stackedByTerms(buckets, { innerKey, labelFor, seriesLabels = {}, palette, getValue }) {
  const pal = palette || dsPalette();
  const val = getValue || ((s) => s.doc_count);
  const rows = buckets || [];
  const seriesKeys = [...new Set(
    rows.flatMap((b) => (b?.[innerKey]?.buckets || []).map((s) => String(s.key))),
  )];
  const labels = rows.map(labelFor);
  const datasets = seriesKeys.map((key, i) => ({
    label: seriesLabels[key] ?? key,
    data: rows.map((b) => {
      const hit = (b?.[innerKey]?.buckets || []).find((s) => String(s.key) === key);
      return hit ? val(hit) : 0;
    }),
    backgroundColor: pal(i),
    borderWidth: 0,
    stack: 'stack',
  }));
  return { labels, datasets };
}

/* ------------------------------------------------------------------ *
 * Mapping DÉCLARATIF agrégation ES → { labels, datasets }
 *
 * Les helpers ci-dessus supposent qu'un bout de JS connaît la forme de la
 * réponse (quel agg, quel sous-agg, quelle valeur). `shapeFromSpec` retire
 * cette connaissance du code : la forme est décrite en YAML (`shape:`), donc
 * un NOUVEAU graphe = une requête ES en config + un bloc YAML, zéro JS.
 *
 * Vocabulaire (voir docs/frontend/dashboard-charts.md) :
 *   buckets     chemin pointé vers les buckets (tableau OU objet de buckets
 *               nommés, cf. agg `filters`).
 *   bucketKeys  ordre explicite des buckets nommés (ex. [s0..s5]).
 *   label       { from: <champ du bucket>, labels: <chemin table de libellés>,
 *                 format: raw|year|month|date|monthsAgo }
 *   value       chemin de la valeur DANS le bucket (défaut `doc_count`),
 *               ex. `revenue.value`, `sum_amount.value`.
 *   series      absent → série unique ; sinon
 *               { from: <nom du sous-agg terms>, labels: <chemin table de
 *                 libellés>, keysFrom: <chemin d'une liste de buckets donnant
 *                 l'ordre canonique des séries>, stacked: true,
 *                 otherLabel: "Non renseigné" }
 *   seriesLabel libellé de légende de la série unique.
 *   colorPerPoint  une couleur DS par point (camembert/donut).
 *   round       false = brut ; true / absent = entier ; nombre = N décimales (ex. 1).
 * ------------------------------------------------------------------ */

const MONTH_LABEL = new Intl.DateTimeFormat('fr-FR', { month: 'short', year: '2-digit' });
const DATE_LABEL = new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'short' });
/**
 * Formateurs numériques, mémoïsés : un `Intl.NumberFormat` coûte cher et une
 * carte KPI se re-rend à chaque refetch.
 *
 * La devise et le nombre de décimales sont des PARAMÈTRES : un coût d'API se
 * lit en dollars et se joue aux centimes (« 0,06 $ »), là où un montant CRM se
 * lit en euros arrondis. Les valeurs par défaut reproduisent exactement le
 * comportement historique (EUR, entier).
 */
const NUMBER_FORMATS = new Map();
function numberFormat(style, currency, decimals) {
  const key = `${style}|${currency}|${decimals}`;
  let formatter = NUMBER_FORMATS.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat('fr-FR', {
      ...(style === 'currency' ? { style: 'currency', currency } : {}),
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    NUMBER_FORMATS.set(key, formatter);
  }
  return formatter;
}

/** Lit une valeur par chemin pointé (« a.b.0.c »). Chemin vide → undefined. */
export function getPath(obj, path) {
  if (!path) return undefined;
  return String(path).split('.').reduce((acc, k) => (acc == null ? undefined : acc[k]), obj);
}

/** Met en forme un libellé d'axe selon `label.format`. */
function formatLabel(value, format, index, total) {
  const raw = value == null ? '' : String(value);
  if (format === 'year') return raw.slice(0, 4);
  if (format === 'monthsAgo') {
    // Buckets nommés (agg `filters`) : aucun horodatage à lire côté réponse —
    // le i-ième bucket = (total - 1 - i) mois avant le mois courant.
    const now = new Date();
    return MONTH_LABEL.format(new Date(now.getFullYear(), now.getMonth() - (total - 1 - index), 1));
  }
  if (format === 'month' || format === 'date') {
    const d = raw ? new Date(raw) : null;
    if (!d || Number.isNaN(d.getTime())) return format === 'month' ? raw.slice(0, 7) : raw;
    return (format === 'month' ? MONTH_LABEL : DATE_LABEL).format(d);
  }
  return raw;
}

/** Normalise les buckets : tableau tel quel, objet nommé → lignes ordonnées. */
function bucketRows(raw, spec) {
  const src = getPath(raw, spec.buckets);
  if (Array.isArray(src)) return src;
  if (src && typeof src === 'object') {
    const keys = Array.isArray(spec.bucketKeys) && spec.bucketKeys.length
      ? spec.bucketKeys
      : Object.keys(src);
    return keys.map((k) => ({ _key: k, ...(src[k] || {}) }));
  }
  return [];
}

/** Chemin des sous-buckets d'une série : `by_stage` ⇒ `by_stage.buckets`. */
function innerPath(from) {
  const p = String(from || '');
  return p.endsWith('.buckets') ? p : `${p}.buckets`;
}

/**
 * Construit `{ labels, datasets }` depuis une réponse ES brute et une spec YAML.
 *
 * @param {object} raw   enveloppe de réponse (`aggregations`, `pagination`, …).
 * @param {object} spec  la spec `shape:` du YAML.
 */
export function shapeFromSpec(raw, spec) {
  const empty = { labels: [], datasets: [] };
  if (!raw || !spec || !spec.buckets) return empty;

  const rows = bucketRows(raw, spec);
  const labelSpec = spec.label || {};
  // Table de libellés optionnelle (ex. `aggregations.by_stage_labels`) : traduit
  // une clé de bucket brute (un TID) en nom lisible, sans JS dédié.
  const labelTable = getPath(raw, labelSpec.labels) || {};
  const labels = rows.map((b, i) => {
    const key = labelSpec.from ? getPath(b, labelSpec.from) : (b.key_as_string ?? b.key ?? b._key);
    return formatLabel(labelTable[String(key)] ?? key, labelSpec.format, i, rows.length);
  });
  // Clé BRUTE de chaque ligne (un TID, un timestamp…), parallèle à `labels` :
  // un clic sur une part de camembert ou une barre simple doit pouvoir dire de
  // quel bucket il parle — le libellé traduit ne l'identifie pas.
  const bucketIds = rows.map((b) => {
    const key = labelSpec.from ? getPath(b, labelSpec.from) : (b.key ?? b._key ?? b.key_as_string);
    return key == null ? '' : String(key);
  });

  const valuePath = spec.value || 'doc_count';
  const roundSpec = spec.round;
  const roundTo = roundSpec === false ? null : (typeof roundSpec === 'number' ? roundSpec : 0);
  const num = (v) => {
    const n = Number(v ?? 0);
    if (!Number.isFinite(n)) return 0;
    if (roundTo === null) return n;
    if (roundTo === 0) return Math.round(n);
    const f = 10 ** roundTo;
    return Math.round(n * f) / f;
  };
  const pal = dsPalette();

  // Série unique (histogramme simple, courbe, camembert).
  if (!spec.series) {
    const color = spec.colorPerPoint ? rows.map((_, i) => pal(i)) : pal(0);
    return {
      labels,
      rowKeys: bucketIds,
      datasets: [{
        label: spec.seriesLabel ?? '',
        data: rows.map((b) => num(getPath(b, valuePath))),
        backgroundColor: color,
        borderColor: color,
        borderWidth: 0,
      }],
    };
  }

  // Multi-séries depuis un sous-agg `terms` (empilé par défaut).
  const inner = (b) => getPath(b, innerPath(spec.series.from)) || [];
  const seriesLabels = getPath(raw, spec.series.labels) || {};
  // Ordre des séries = ordre des COULEURS et de la légende. Déduit des lignes,
  // il dépend de la fenêtre affichée : ES trie les sous-buckets par doc_count
  // DANS chaque bucket, donc un mois qui entre ou sort décale l'ordre et un
  // propriétaire change de couleur d'un affichage à l'autre.
  // `keysFrom` pointe un agg de TÊTE (même `terms`, calculé sur tout le jeu
  // filtré) : l'ordre devient global et stable. Les clés vues dans les lignes
  // mais absentes de cette liste (agg de tête tronqué par `size`) sont ajoutées
  // à la suite — jamais perdues.
  const canonical = getPath(raw, spec.series.keysFrom);
  const rowKeys = [...new Set(rows.flatMap((b) => inner(b).map((s) => String(s.key))))];
  const seriesKeys = Array.isArray(canonical)
    ? [...new Set([...canonical.map((b) => String(b?.key)), ...rowKeys])].filter((k) => rowKeys.includes(k))
    : rowKeys;
  const stacked = spec.series.stacked !== false;

  const datasets = seriesKeys.map((key, i) => ({
    // La CLÉ brute voyage avec la série : un clic sur une barre ou une cellule
    // doit pouvoir dire de quelle étape il parle, et le libellé ne l'identifie
    // pas (deux pipelines peuvent porter une étape homonyme).
    key,
    label: seriesLabels[key] ?? key,
    data: rows.map((b) => {
      const hit = inner(b).find((s) => String(s.key) === key);
      return hit ? num(getPath(hit, valuePath)) : 0;
    }),
    backgroundColor: pal(i),
    borderColor: pal(i),
    borderWidth: 0,
    ...(stacked ? { stack: 'stack' } : {}),
  }));

  // Reste à ventiler. Un sous-agg `terms` ne voit QUE les documents dont le
  // champ est renseigné : sans cette série, une transaction sans BU disparaît
  // du graphe, qui affiche « aucune donnée » alors que la requête a bien ramené
  // des documents. `otherLabel` réinjecte l'écart entre la valeur du bucket
  // parent et la somme de ses sous-buckets.
  //
  // Borné à 0 pour deux cas : sur un champ MULTI-valué la somme des
  // sous-buckets peut DÉPASSER le `doc_count` du parent (un document compté
  // dans deux termes) ; et si `value` pointe une métrique absente du bucket
  // parent (somme agrégée seulement dans les sous-buckets), l'écart vaut 0 —
  // aucune série fantôme n'apparaît.
  if (spec.series.otherLabel) {
    const rest = rows.map((b) => {
      const covered = inner(b).reduce((acc, sub) => acc + num(getPath(sub, valuePath)), 0);
      return Math.max(0, num(getPath(b, valuePath)) - covered);
    });
    if (rest.some((v) => v > 0)) {
      const color = pal(datasets.length);
      datasets.push({
        // Pas de `key` : cette série n'est pas un terme de l'agrégation mais un
        // reste calculé. Un clic dessus n'a donc rien à filtrer ; `rest` la
        // signale (une série UNIQUE n'a pas de clé non plus, mais se filtre
        // par la clé de ligne).
        rest: true,
        label: spec.series.otherLabel,
        data: rest,
        backgroundColor: color,
        borderColor: color,
        borderWidth: 0,
        ...(stacked ? { stack: 'stack' } : {}),
      });
    }
  }

  return { labels, rowKeys: bucketIds, datasets };
}

/* ------------------------------------------------------------------ *
 * KPI déclaratifs (mêmes réponses ES, rendu en cartes `.stat-card`).
 * ------------------------------------------------------------------ */

/** Valeur numérique brute d'une spec KPI (`path` seul, ou `reduce` sur buckets). */
function kpiNumber(raw, spec) {
  const src = getPath(raw, spec?.path);
  if (!spec?.reduce) {
    const n = Number(src);
    return Number.isFinite(n) ? n : null;
  }
  const rows = Array.isArray(src)
    ? src
    : (src && typeof src === 'object' ? Object.values(src) : []);
  if (spec.reduce === 'count') return rows.length;
  const values = rows
    .map((b) => Number(getPath(b, spec.of || 'doc_count') ?? 0))
    .filter(Number.isFinite);
  if (!values.length) return 0;
  switch (spec.reduce) {
    case 'avg': return values.reduce((a, b) => a + b, 0) / values.length;
    case 'max': return Math.max(...values);
    case 'min': return Math.min(...values);
    default: return values.reduce((a, b) => a + b, 0);
  }
}

/**
 * Rend un nombre selon `format` : number (défaut) | currency | percent | raw.
 *
 * `opts.currency` (défaut EUR) et `opts.decimals` (défaut 0) sont déclarés par
 * la carte KPI : un coût d'API se lit « 0,06 $ », un montant CRM « 1 200 € ».
 */
export function formatKpi(value, format, opts = {}) {
  const n = Number(value);
  if (value == null || !Number.isFinite(n)) return '—';
  const decimals = Number.isFinite(Number(opts.decimals)) ? Number(opts.decimals) : 0;
  if (format === 'currency') return numberFormat('currency', opts.currency || 'EUR', decimals).format(n);
  if (format === 'percent') return `${Math.round(n * 100)} %`;
  if (format === 'raw') return String(n);
  return numberFormat('decimal', null, decimals).format(n);
}

/**
 * Évalue une carte KPI déclarative sur une réponse ES brute.
 * `{ path, reduce?, of?, dividedBy?: {path, reduce?, of?}, format?, currency?,
 *   decimals? }`
 * → chaîne prête à afficher (« — » si la donnée manque).
 */
export function evaluateKpi(raw, card) {
  if (!raw || !card) return '—';
  let value = kpiNumber(raw, card);
  if (value == null) return '—';
  if (card.dividedBy) {
    const divisor = kpiNumber(raw, card.dividedBy);
    value = divisor ? value / divisor : 0;
  }
  return formatKpi(value, card.format, { currency: card.currency, decimals: card.decimals });
}
